import { FolderItem, Item } from "@/types/fse";
import { usePagination } from "@/views/drive/hooks/use-paginations";
import { useGetFileDetailsQuery } from "@/api/files";
import * as React from "react";
import { UploadStatusMessage, useUploadStatus } from "@/hooks/use-upload-status";
import { TxType } from "@/types/transaction";
import { useSearchParams } from "react-router";
import { isDirectoryUploadFolderProgressEvent, isDriveProgressEvent } from "../driveProgressEvents";

const DRIVE_REFETCH_TX_TYPES = [TxType.UPLOAD, TxType.CONTRACT_FAMILY_GROUPING];

export const isFolderItem = (item: Item): item is FolderItem => item.type === "folder";

interface DriveSearchParams {
  folderId?: string | undefined;
  only_shared?: boolean;
}

export const useDriveList = ({ folderId, only_shared }: DriveSearchParams = {}) => {
  const { isLoading, isFetching, data, error, refetch } = useGetFileDetailsQuery({ fse_id: folderId, only_shared });
  const [searchParams] = useSearchParams();
  const txId = searchParams.get("tx_id");

  const displayItems = React.useMemo(
    () => (data && !Array.isArray(data) && isFolderItem(data) ? Object.values(data.items) : []),
    [data],
  );

  // Debounced refetch so bursts of websocket events collapse into a single /ls call.
  const refetchTimerRef = React.useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const scheduleRefetch = React.useCallback(
    (delay: number) => {
      clearTimeout(refetchTimerRef.current);
      refetchTimerRef.current = setTimeout(() => {
        try {
          refetch();
        } catch (e) {
          console.error("Error refetching data:", e);
        }
      }, delay);
    },
    [refetch],
  );

  // fse ids we've already refreshed the list for, so an uploaded file triggers exactly
  // one early refetch when it first appears (not on every subsequent progress tick).
  const refreshedFseIdsRef = React.useRef<Set<string>>(new Set());

  const onUploadStatusChange = React.useCallback(
    (event: UploadStatusMessage) => {
      // Early refresh: as soon as an uploaded file first has a filesystem entry (parsed),
      // show it in the drive list — don't wait for full ingestion (progress >= 1.0) or a
      // manual reload. Fires once per newly-seen fse.
      if (
        (event.type === "document" || isDirectoryUploadFolderProgressEvent(event)) &&
        DRIVE_REFETCH_TX_TYPES.includes(event.tx_type) &&
        event.fse_ids.some(id => !refreshedFseIdsRef.current.has(id))
      ) {
        event.fse_ids.forEach(id => refreshedFseIdsRef.current.add(id));
        scheduleRefetch(500);
      }

      // Final refresh: once ingestion completes, refetch to pull final metadata (summary…).
      if (isDriveProgressEvent(event) && DRIVE_REFETCH_TX_TYPES.includes(event.tx_type) && event.progress >= 1.0) {
        scheduleRefetch(3500);
      }

      if (event.tx_id === txId) {
        scheduleRefetch(3500);
      }
    },
    [scheduleRefetch, txId],
  );

  useUploadStatus(onUploadStatusChange);

  const pagination = usePagination(displayItems || []);

  return {
    isLoading,
    isFetching,
    error,
    refetch,
    ...pagination,
    data,
  };
};
